import { useEffect, useMemo, useRef, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import { Button } from '../../../Common/Button'
import {
  type ChecklistPayloadGroup,
  type ChecklistPayload,
  normalizeChecklistPayload,
} from './checklistPayload'

export type { ChecklistPayloadGroup, ChecklistPayload }

type ChecklistPayloadEditorProps = {
  value: ChecklistPayload
  onChange: (next: ChecklistPayload) => void
}

type ItemDrag = { groupIndex: number; itemIndex: number }

const withOptionalList = (payload: ChecklistPayload, key: 'proofGates' | 'ownerRoles', values: string[]) => {
  if (values.length === 0) {
    const next = { ...payload }
    delete next[key]
    return next
  }
  return { ...payload, [key]: values }
}

const reorder = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

const serializeChips = (items: string[] | undefined) => (items ?? []).join(', ')
const parseChips = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)

export const ChecklistPayloadEditor = ({ value, onChange }: ChecklistPayloadEditorProps) => {
  const emittedInitialValueRef = useRef(false)
  const draggedGroupIndexRef = useRef<number | null>(null)
  const draggedItemRef = useRef<ItemDrag | null>(null)
  const payload = useMemo(() => normalizeChecklistPayload(value), [value])

  useEffect(() => {
    if (emittedInitialValueRef.current) return
    emittedInitialValueRef.current = true
    onChange(payload)
  }, [onChange, payload])

  const emit = (next: ChecklistPayload) => onChange(normalizeChecklistPayload(next))

  const updateGroup = (groupIndex: number, nextGroup: ChecklistPayloadGroup) => {
    emit({
      ...payload,
      groups: payload.groups.map((group, index) => index === groupIndex ? nextGroup : group),
    })
  }

  const addGroup = () => emit({
    ...payload,
    groups: [...payload.groups, { title: 'New group', items: [] }],
  })

  const deleteGroup = (groupIndex: number) => emit({
    ...payload,
    groups: payload.groups.filter((_, index) => index !== groupIndex),
  })

  const addItem = (groupIndex: number) => {
    const group = payload.groups[groupIndex]
    if (!group) return
    updateGroup(groupIndex, { ...group, items: [...group.items, 'New item'] })
  }

  const updateItem = (groupIndex: number, itemIndex: number, nextItem: string) => {
    const group = payload.groups[groupIndex]
    if (!group) return
    updateGroup(groupIndex, {
      ...group,
      items: group.items.map((item, index) => index === itemIndex ? nextItem : item),
    })
  }

  const deleteItem = (groupIndex: number, itemIndex: number) => {
    const group = payload.groups[groupIndex]
    if (!group) return
    updateGroup(groupIndex, {
      ...group,
      items: group.items.filter((_, index) => index !== itemIndex),
    })
  }

  const reorderGroup = (toIndex: number) => {
    const fromIndex = draggedGroupIndexRef.current
    draggedGroupIndexRef.current = null
    if (fromIndex === null) return
    emit({ ...payload, groups: reorder(payload.groups, fromIndex, toIndex) })
  }

  const reorderItem = (toGroupIndex: number, toItemIndex: number) => {
    const from = draggedItemRef.current
    draggedItemRef.current = null
    if (!from || from.groupIndex !== toGroupIndex) return
    const group = payload.groups[toGroupIndex]
    if (!group) return
    updateGroup(toGroupIndex, { ...group, items: reorder(group.items, from.itemIndex, toItemIndex) })
  }

  const updateChipList = (key: 'proofGates' | 'ownerRoles', nextValue: string) => {
    emit(withOptionalList(payload, key, parseChips(nextValue)))
  }

  return (
    <section className="checklistPayloadEditor" aria-label="Checklist payload editor">
      <div className="checklistPayloadEditor__groups">
        {payload.groups.map((group, groupIndex) => (
          <article
            className="checklistPayloadEditor__group"
            key={`group-${groupIndex}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => reorderGroup(groupIndex)}
          >
            <header>
              <button
                type="button"
                className="checklistPayloadEditor__drag"
                draggable
                aria-label={`Drag group ${groupIndex + 1}`}
                onDragStart={() => { draggedGroupIndexRef.current = groupIndex }}
              >
                <DragIndicatorRoundedIcon fontSize="inherit" />
              </button>
              <label>
                <span>Group title</span>
                <input
                  aria-label={`Group ${groupIndex + 1} title`}
                  value={group.title}
                  onChange={(event) => updateGroup(groupIndex, { ...group, title: event.target.value })}
                />
              </label>
              <button
                type="button"
                className="checklistPayloadEditor__iconButton"
                aria-label={`Delete group ${groupIndex + 1}`}
                onClick={() => deleteGroup(groupIndex)}
              >
                <DeleteRoundedIcon fontSize="inherit" />
              </button>
            </header>
            <div className="checklistPayloadEditor__items">
              {group.items.map((item, itemIndex) => (
                <div
                  className="checklistPayloadEditor__item"
                  key={`item-${groupIndex}-${itemIndex}`}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => reorderItem(groupIndex, itemIndex)}
                >
                  <button
                    type="button"
                    className="checklistPayloadEditor__drag"
                    draggable
                    aria-label={`Drag item ${itemIndex + 1} in group ${groupIndex + 1}`}
                    onDragStart={() => { draggedItemRef.current = { groupIndex, itemIndex } }}
                  >
                    <DragIndicatorRoundedIcon fontSize="inherit" />
                  </button>
                  <input
                    aria-label={`Item ${itemIndex + 1} in group ${groupIndex + 1}`}
                    value={item}
                    onChange={(event) => updateItem(groupIndex, itemIndex, event.target.value)}
                  />
                  <button
                    type="button"
                    className="checklistPayloadEditor__iconButton"
                    aria-label={`Delete item ${itemIndex + 1} in group ${groupIndex + 1}`}
                    onClick={() => deleteItem(groupIndex, itemIndex)}
                  >
                    <DeleteRoundedIcon fontSize="inherit" />
                  </button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              icon={<AddRoundedIcon fontSize="inherit" />}
              onClick={() => addItem(groupIndex)}
            >
              Add item
            </Button>
          </article>
        ))}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        icon={<AddRoundedIcon fontSize="inherit" />}
        onClick={addGroup}
      >
        Add group
      </Button>
      <div className="checklistPayloadEditor__chipFields">
        <ChipInput
          label="Proof gates"
          value={payload.proofGates}
          onChange={(next) => updateChipList('proofGates', next)}
        />
        <ChipInput
          label="Owner roles"
          value={payload.ownerRoles}
          onChange={(next) => updateChipList('ownerRoles', next)}
        />
      </div>
    </section>
  )
}

type ChipInputProps = {
  label: string
  value?: string[]
  onChange: (next: string) => void
}

const ChipInput = ({ label, value, onChange }: ChipInputProps) => {
  const [draft, setDraft] = useState(serializeChips(value))
  const serializedValue = serializeChips(value)

  const [prevSerializedValue, setPrevSerializedValue] = useState(serializedValue)
  if (prevSerializedValue !== serializedValue) {
    setPrevSerializedValue(serializedValue)
    if (serializeChips(parseChips(draft)) !== serializedValue) {
      setDraft(serializedValue)
    }
  }

  const chips = parseChips(draft)

  return (
    <label className="checklistPayloadEditor__chipInput">
      <span>{label}</span>
      <div className="checklistPayloadEditor__chips">
        {chips.map((chip) => <i key={chip}>{chip}</i>)}
        <input
          aria-label={label}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value)
            onChange(event.target.value)
          }}
        />
      </div>
    </label>
  )
}
